/**
 * Magic Bytes (File Signature) Validation
 *
 * Validates file type by reading the first bytes of the file (file signature)
 * instead of trusting the file extension or MIME type.
 *
 * Prevents attackers from uploading malicious files disguised as images/videos.
 *
 * References:
 * - https://en.wikipedia.org/wiki/List_of_file_signatures
 * - https://www.garykessler.net/library/file_sigs.html
 */

import { devLog } from '@/utils/logger';

/**
 * File signature definition
 */
interface FileSignature {
  mimeType: string;
  signatures: number[][];
  offset?: number;
}

/**
 * Known file signatures (magic bytes)
 *
 * Each signature is an array of bytes (in hex) that identify the file type.
 * Most file formats have unique signatures at the beginning of the file.
 */
const FILE_SIGNATURES: FileSignature[] = [
  // Images
  {
    mimeType: 'image/jpeg',
    signatures: [
      // FF D8 FF is the definitive JPEG Start-of-Image marker shared by every
      // valid JPEG. The 4th byte is just the first APP/quantization marker
      // and legitimately varies (E0 JFIF, E1 Exif, ED Photoshop/IPTC,
      // EE Adobe, DB raw, etc.) — design tools like Photoshop/Canva commonly
      // emit E0/ED/EE. Match the 3-byte prefix only, same as the backend
      // (backend/src/utils/file-validation.ts).
      [0xFF, 0xD8, 0xFF],
    ],
  },
  {
    mimeType: 'image/png',
    signatures: [
      [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], // PNG
    ],
  },
  {
    mimeType: 'image/gif',
    signatures: [
      [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], // GIF87a
      [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], // GIF89a
    ],
  },
  {
    mimeType: 'image/webp',
    signatures: [
      [0x52, 0x49, 0x46, 0x46], // Check just RIFF prefix for WebP (full: RIFF....WEBP)
    ],
  },
  {
    mimeType: 'image/bmp',
    signatures: [
      [0x42, 0x4D], // BMP
    ],
  },

  // Videos
  //
  // NOTE: MP4/MOV/M4V (and audio variants like M4A) are all ISO Base Media
  // File Format containers identified by an `ftyp` box. That box is USUALLY
  // at byte offset 4, but not always — some encoders/editors emit a leading
  // filler atom (`wide`, `free`, `skip`, `pnot`) or even `mdat` before
  // `ftyp`. Rather than hardcoding "ftyp at offset 4" (which rejects
  // legitimate files), the whole ftyp family is detected by
  // `detectFtypFamilyVideo()` below and canonicalized to a single
  // `video/mp4` result — see `detectFileTypeByMagicBytes()`.
  {
    mimeType: 'video/x-msvideo',
    signatures: [
      [0x52, 0x49, 0x46, 0x46], // AVI RIFF prefix (full: RIFF....AVI )
    ],
  },
  {
    mimeType: 'video/webm',
    signatures: [
      [0x1A, 0x45, 0xDF, 0xA3], // WebM
    ],
  },

  // Documents
  {
    mimeType: 'application/pdf',
    signatures: [
      [0x25, 0x50, 0x44, 0x46, 0x2D], // %PDF- (hex: 25 50 44 46 2D)
    ],
  },
];

/**
 * Read the first N bytes of a file
 */
async function readFileBytes(file: File, numBytes: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    // Read only the first N bytes
    const blob = file.slice(0, numBytes);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Check if byte array matches a signature pattern
 *
 * @param bytes - Actual file bytes
 * @param signature - Expected signature pattern (undefined means "any byte")
 * @param offset - Byte offset to start matching (default: 0)
 */
function matchesSignature(
  bytes: Uint8Array,
  signature: (number | undefined)[],
  offset: number = 0
): boolean {
  // Check if we have enough bytes
  if (bytes.length < offset + signature.length) {
    return false;
  }

  // Compare each byte
  for (let i = 0; i < signature.length; i++) {
    const expectedByte = signature[i];
    const actualByte = bytes[offset + i];

    // undefined means "any byte is acceptable"
    if (expectedByte !== undefined && expectedByte !== actualByte) {
      return false;
    }
  }

  return true;
}

/**
 * ASCII bytes for the ISO-BMFF `ftyp` box type, used to identify MP4/MOV/M4V
 * containers regardless of which "brand" (isom, qt  , M4V , mp42, ...) wrote
 * them.
 */
const FTYP_BOX_TYPE = [0x66, 0x74, 0x79, 0x70]; // 'ftyp'

/**
 * Box types that legitimately precede `ftyp` in some MP4/MOV files (padding,
 * QuickTime print info, or — rarely — a leading `mdat`). Recognizing these
 * lets the atom walk skip past them instead of giving up at offset 4.
 */
const FTYP_LEADING_ATOM_TYPES = new Set(['wide', 'free', 'skip', 'pnot', 'mdat']);

/** Number of leading atoms we're willing to hop over before giving up. */
const FTYP_MAX_ATOM_HOPS = 5;

/** Read a big-endian uint32 at `offset`, or null if out of bounds. */
function readUint32BE(bytes: Uint8Array, offset: number): number | null {
  if (bytes.length < offset + 4) {
    return null;
  }
  // `>>> 0` keeps the result unsigned (box sizes near the start of a file
  // are always small, but this guards against sign-bit weirdness anyway).
  return (
    ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0
  );
}

/** Read a 4-byte ASCII box type at `offset`, or null if out of bounds. */
function readBoxType(bytes: Uint8Array, offset: number): string | null {
  if (bytes.length < offset + 4) {
    return null;
  }
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

/**
 * Detect whether `bytes` (the leading chunk of a file) is an ISO-BMFF
 * container carrying an `ftyp` box — i.e. MP4, MOV/QuickTime, or M4V/M4A.
 *
 * Handles the common case (`ftyp` at byte offset 4) as well as files that
 * lead with a filler/padding atom (`wide`, `free`, `skip`, `pnot`, `mdat`)
 * before `ftyp`, by walking the atom chain up to a few hops. Falls back to
 * a plain scan for the ASCII `ftyp` marker within the read window in case
 * an encoder emits a box we don't recognize by name.
 */
function detectFtypFamilyVideo(bytes: Uint8Array): boolean {
  let pos = 0;

  for (let hop = 0; hop < FTYP_MAX_ATOM_HOPS; hop++) {
    const boxSize = readUint32BE(bytes, pos);
    const boxType = readBoxType(bytes, pos + 4);

    if (boxType === null) {
      break;
    }

    if (boxType === 'ftyp') {
      return true;
    }

    // Only hop over a known filler atom, and only if its declared size is
    // sane (a real atom header is at least 8 bytes; cap the hop so we never
    // walk past our read window looking for the next box).
    if (FTYP_LEADING_ATOM_TYPES.has(boxType) && boxSize !== null && boxSize >= 8) {
      pos += boxSize;
      continue;
    }

    break;
  }

  // Fallback: scan the read window for the ASCII 'ftyp' marker directly.
  // This catches box layouts the atom walk doesn't model (e.g. an unknown
  // leading atom type) while remaining safe for non-media files — the
  // 4-byte 'ftyp' sequence is not something arbitrary binary data (or a
  // renamed executable) is realistically going to contain by chance.
  const scanLimit = bytes.length - FTYP_BOX_TYPE.length;
  for (let i = 0; i <= scanLimit; i++) {
    if (matchesSignature(bytes, FTYP_BOX_TYPE, i)) {
      return true;
    }
  }

  return false;
}

/**
 * Detect file MIME type by reading magic bytes
 *
 * @param file - File to analyze
 * @returns Detected MIME type or null if unknown
 */
export async function detectFileTypeByMagicBytes(file: File): Promise<string | null> {
  try {
    // Read first 64 bytes — enough for every signature below plus the
    // ftyp-family atom walk/scan (which needs a bit more room than a plain
    // fixed-offset check to skip past leading filler atoms).
    const bytes = await readFileBytes(file, 64);

    // Check against all known fixed-signature formats (images, PDF, WebM,
    // AVI/RIFF).
    for (const fileSignature of FILE_SIGNATURES) {
      const offset = fileSignature.offset || 0;

      // Try all signature variations for this file type
      for (const signature of fileSignature.signatures) {
        if (matchesSignature(bytes, signature, offset)) {
          return fileSignature.mimeType;
        }
      }
    }

    // MP4/MOV/M4V — canonicalize the whole ftyp family to a single type.
    // Browsers/OSes report inconsistent MIME types for these (video/mp4,
    // video/quicktime, video/x-m4v, application/octet-stream), so treat
    // content detection as authoritative and return one canonical result.
    if (detectFtypFamilyVideo(bytes)) {
      return 'video/mp4';
    }

    // Unknown file type
    return null;
  } catch (error) {
    devLog.error('Failed to detect file type:', error);
    return null;
  }
}

/**
 * Validate file type using magic bytes
 *
 * @param file - File to validate
 * @param allowedTypes - Allowed MIME types
 * @returns true if file type is allowed, false otherwise
 */
export async function validateFileByMagicBytes(
  file: File,
  allowedTypes: string[]
): Promise<boolean> {
  const detectedType = await detectFileTypeByMagicBytes(file);

  if (!detectedType) {
    // Unknown file type - reject
    return false;
  }

  // Check if detected type is in allowed list
  return allowedTypes.includes(detectedType);
}

/**
 * Browser-reported MIME types that give us no real information about the
 * file's content (missing, or the generic fallback browsers/OSes use when
 * they don't recognize a file by extension/registration).
 */
const GENERIC_CLAIMED_TYPES = new Set(['', 'application/octet-stream']);

/**
 * Claimed MIME types that all describe the same ftyp-family container
 * (MP4/MOV/M4V). Different browsers and operating systems report different
 * labels for the exact same bytes — a `.mov` is `video/quicktime` on some
 * platforms and `video/mp4` on others, `.m4v` is often `video/x-m4v` — so
 * none of these should be flagged as a "mismatch" against a detected
 * `video/mp4`.
 */
const FTYP_FAMILY_CLAIMED_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/mpeg',
]);

/**
 * Validate that file extension matches actual file content
 *
 * Prevents attacks like:
 * - malware.exe renamed to malware.jpg
 * - virus.sh renamed to virus.png
 *
 * @param file - File to validate
 * @returns true if extension matches content, false otherwise
 */
export async function validateFileExtensionMatchesContent(file: File): Promise<boolean> {
  const detectedType = await detectFileTypeByMagicBytes(file);

  if (!detectedType) {
    return false; // Unknown file type — genuine mismatch (or unrecognized content)
  }

  // Generic/absent browser label: it carries no claim to validate against,
  // so trust the actual detected content type instead of flagging it as a
  // "mismatch".
  if (GENERIC_CLAIMED_TYPES.has(file.type)) {
    return true;
  }

  // ftyp-family container (MP4/MOV/M4V): any claimed type within the same
  // family is consistent with the detected content, regardless of which
  // exact label the browser/OS chose.
  if (detectedType === 'video/mp4' && FTYP_FAMILY_CLAIMED_TYPES.has(file.type)) {
    return true;
  }

  // Check if browser's MIME type (from extension) matches detected type
  // Note: 'image/jpg' is not a standard MIME type, it should be 'image/jpeg'
  // Handle this common case
  const normalizedClaimedType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;

  return normalizedClaimedType === detectedType;
}
